import React, { useState, useEffect } from 'react';
import {
  ChartBarIcon,
  CalendarIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { Line } from 'react-chartjs-2';
import { questionnaireApi } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { useUser } from '../contexts/UserContext';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface THIQuestion {
  id: number;
  question: string;
  category: 'functional' | 'emotional' | 'catastrophic';
}

interface QuestionnaireResult {
  id: number;
  date: string;
  total_score: number;
  answers: Record<string, number>;
}

const THI_QUESTIONS: THIQuestion[] = [
  // Functional subscale (1-12)
  { id: 1, question: "Because of your tinnitus, is it difficult for you to concentrate?", category: 'functional' },
  { id: 2, question: "Does the loudness of your tinnitus make it difficult for you to hear people?", category: 'functional' },
  { id: 3, question: "Does your tinnitus make you angry?", category: 'functional' },
  { id: 4, question: "Does your tinnitus make you feel confused?", category: 'functional' },
  { id: 5, question: "Because of your tinnitus, do you feel desperate?", category: 'functional' },
  { id: 6, question: "Do you complain a great deal about your tinnitus?", category: 'functional' },
  { id: 7, question: "Because of your tinnitus, do you have trouble falling asleep at night?", category: 'functional' },
  { id: 8, question: "Do you feel as though you cannot escape your tinnitus?", category: 'functional' },
  { id: 9, question: "Does your tinnitus interfere with your ability to enjoy social activities (such as going out to dinner, to movies)?", category: 'functional' },
  { id: 10, question: "Because of your tinnitus, do you feel frustrated?", category: 'functional' },
  { id: 11, question: "Because of your tinnitus, do you feel that you have a terrible disease?", category: 'functional' },
  { id: 12, question: "Does your tinnitus make it difficult for you to enjoy life?", category: 'functional' },
  
  // Emotional subscale (13-24)
  { id: 13, question: "Does your tinnitus make you feel anxious?", category: 'emotional' },
  { id: 14, question: "Because of your tinnitus, do you feel that you have no control over your life?", category: 'emotional' },
  { id: 15, question: "Because of your tinnitus, do you often feel tired?", category: 'emotional' },
  { id: 16, question: "Because of your tinnitus, do you feel depressed?", category: 'emotional' },
  { id: 17, question: "Does your tinnitus make you feel irritable?", category: 'emotional' },
  { id: 18, question: "Does your tinnitus make you feel that life is not worth living?", category: 'emotional' },
  { id: 19, question: "Does your tinnitus interfere with your job or household responsibilities?", category: 'emotional' },
  { id: 20, question: "Because of your tinnitus, do you find that you are often irritable?", category: 'emotional' },
  { id: 21, question: "Because of your tinnitus, is it difficult for you to read?", category: 'emotional' },
  { id: 22, question: "Does your tinnitus make you feel uncomfortable when you go to bed at night?", category: 'emotional' },
  { id: 23, question: "Does your tinnitus make you feel that you have no control over your life?", category: 'emotional' },
  { id: 24, question: "Do you feel as though you cannot escape your tinnitus?", category: 'emotional' },
  
  // Catastrophic subscale (25-25)
  { id: 25, question: "Because of your tinnitus, do you feel that you have a terrible disease?", category: 'catastrophic' }
];

const SCORE_OPTIONS = [
  { value: 0, label: 'No' },
  { value: 1, label: 'Sometimes' },
  { value: 2, label: 'Yes' }
];

const getSeverityLevel = (score: number): { level: string; color: string; description: string } => {
  if (score <= 16) return { level: 'Slight', color: 'text-green-600', description: 'Minimal impact on daily life' };
  if (score <= 36) return { level: 'Mild', color: 'text-yellow-600', description: 'Some impact on daily activities' };
  if (score <= 56) return { level: 'Moderate', color: 'text-orange-600', description: 'Significant impact on daily life' };
  if (score <= 76) return { level: 'Severe', color: 'text-red-600', description: 'Major impact on daily activities' };
  return { level: 'Catastrophic', color: 'text-red-800', description: 'Extreme impact on all aspects of life' };
};

const Questionnaire: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const { isAuthenticated } = useUser();
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [totalScore, setTotalScore] = useState(0);
  const [history, setHistory] = useState<QuestionnaireResult[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  // Load mock history data
  useEffect(() => {
    const mockHistory: QuestionnaireResult[] = [
      {
        id: 1,
        date: '2024-01-15',
        total_score: 42,
        answers: {}
      },
      {
        id: 2,
        date: '2024-02-15',
        total_score: 38,
        answers: {}
      },
      {
        id: 3,
        date: '2024-03-15',
        total_score: 35,
        answers: {}
      }
    ];
    setHistory(mockHistory);
  }, []);

  const handleAnswer = (questionId: number, score: number) => {
    const updatedAnswers = { ...answers, [questionId]: score };
    setAnswers(updatedAnswers);

    if (currentQuestion < THI_QUESTIONS.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      const total = Object.values(updatedAnswers).reduce((sum, s) => sum + s, 0);
      setTotalScore(total);
      setIsCompleted(true);
    }
  };

  const saveResults = async () => {
    if (!isAuthenticated) {
      showError('Sign in required', 'Please sign in to save your results.');
      return;
    }
    try {
      await questionnaireApi.create({ total_score: totalScore, answers });
      setIsSaved(true);
      showSuccess('Results saved', 'Your THI questionnaire results have been saved.');
    } catch {
      showError('Save failed', 'Could not save your results. Please try again.');
    }
  };

  const resetQuestionnaire = () => {
    setAnswers({});
    setCurrentQuestion(0);
    setIsCompleted(false);
    setTotalScore(0);
  };

  const severity = getSeverityLevel(totalScore);

  // Chart data for history
  const chartData = {
    labels: history.map(h => new Date(h.date).toLocaleDateString()),
    datasets: [
      {
        label: 'THI Score',
        data: history.map(h => h.total_score),
        borderColor: '#6FB1A0',
        backgroundColor: '#6FB1A0',
        tension: 0.1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Tinnitus Handicap Inventory Score Over Time',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: 'THI Score'
        }
      },
      x: {
        title: {
          display: true,
          text: 'Date'
        }
      }
    },
  };

  if (showHistory) {
    if (!isAuthenticated) {
      return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
          <CalendarIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-auris-dark mb-2">Sign in to view your history</h2>
          <p className="text-gray-500 text-sm mb-6">Your past questionnaire scores will appear here once you sign in.</p>
          <button
            onClick={() => setShowHistory(false)}
            className="bg-auris-teal text-white px-4 py-2 rounded-lg hover:bg-opacity-90 transition-colors"
          >
            Back to Questionnaire
          </button>
        </div>
      );
    }
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-auris-dark">
            Questionnaire History
          </h1>
          <button
            onClick={() => setShowHistory(false)}
            className="bg-auris-teal text-white px-4 py-2 rounded-lg hover:bg-opacity-90 transition-colors"
          >
            Back to Questionnaire
          </button>
        </div>

        {history.length > 0 ? (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <ChartBarIcon className="w-5 h-5 mr-2" />
                Score Trend
              </h2>
              <Line data={chartData} options={chartOptions} />
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <CalendarIcon className="w-5 h-5 mr-2" />
                Previous Results
              </h2>
              <div className="space-y-3">
                {history.map((result) => {
                  const resultSeverity = getSeverityLevel(result.total_score);
                  return (
                    <div key={result.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div>
                        <span className="font-medium">{new Date(result.date).toLocaleDateString()}</span>
                        <span className={`ml-2 px-2 py-1 rounded text-sm font-medium ${resultSeverity.color} bg-opacity-20`}>
                          {resultSeverity.level}
                        </span>
                      </div>
                      <div className="text-lg font-bold text-auris-teal">
                        {result.total_score}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <CalendarIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No History Yet</h3>
            <p className="text-gray-600">Complete your first questionnaire to start tracking your progress.</p>
          </div>
        )}
      </div>
    );
  }

  if (isCompleted) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-8">
          <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-auris-dark mb-4">
            Questionnaire Complete
          </h1>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-6">
          <div className="text-center mb-6">
            <div className="text-4xl font-bold text-auris-teal mb-2">
              {totalScore}
            </div>
            <div className="text-lg text-gray-600 mb-2">Total THI Score</div>
            <div className={`text-xl font-semibold ${severity.color} mb-2`}>
              {severity.level} Severity
            </div>
            <div className="text-gray-600">
              {severity.description}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-auris-teal">
                {Object.values(answers).filter(a => a === 0).length}
              </div>
              <div className="text-sm text-gray-600">No responses</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-auris-teal">
                {Object.values(answers).filter(a => a === 1).length}
              </div>
              <div className="text-sm text-gray-600">Sometimes responses</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-auris-teal">
                {Object.values(answers).filter(a => a === 2).length}
              </div>
              <div className="text-sm text-gray-600">Yes responses</div>
            </div>
          </div>

          <div className="flex justify-center space-x-4">
            <div className="flex flex-col items-center">
              {isSaved ? (
                <span className="flex items-center space-x-1 text-green-600 font-medium py-3">
                  <CheckCircleIcon className="w-5 h-5" />
                  <span>Saved!</span>
                </span>
              ) : (
                <button
                  onClick={saveResults}
                  disabled={!isAuthenticated}
                  className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                    isAuthenticated
                      ? 'bg-auris-teal text-white hover:bg-opacity-90'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Save Results
                </button>
              )}
              {!isAuthenticated && !isSaved && (
                <p className="text-xs text-gray-400 mt-1">Sign in to save</p>
              )}
            </div>
            <button
              onClick={resetQuestionnaire}
              className="bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-300 transition-colors"
            >
              Take Again
            </button>
            {isAuthenticated && (
              <button
                onClick={() => setShowHistory(true)}
                className="bg-auris-blue text-auris-dark px-6 py-3 rounded-lg font-medium hover:bg-opacity-80 transition-colors"
              >
                View History
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const currentQ = THI_QUESTIONS[currentQuestion];
  const progress = ((currentQuestion + 1) / THI_QUESTIONS.length) * 100;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-auris-dark mb-4">
          Tinnitus Handicap Inventory
        </h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          This questionnaire will help assess how tinnitus affects your daily life. 
          Please answer each question honestly based on your experience.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">
              Question {currentQuestion + 1} of {THI_QUESTIONS.length}
            </span>
            <span className="text-sm text-gray-500">
              {Math.round(progress)}% complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-auris-teal h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Question */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-auris-dark mb-4">
            {currentQ.question}
          </h2>
          
          <div className="space-y-3">
            {SCORE_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => handleAnswer(currentQ.id, option.value)}
                className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-auris-teal hover:bg-auris-blue hover:bg-opacity-10 transition-colors"
              >
                <div className="flex items-center">
                  <div className="w-4 h-4 border-2 border-gray-300 rounded-full mr-3"></div>
                  <span className="font-medium">{option.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Category indicator */}
        <div className="text-center">
          <span className="inline-block px-3 py-1 bg-auris-blue bg-opacity-20 text-auris-dark rounded-full text-sm font-medium">
            {currentQ.category.charAt(0).toUpperCase() + currentQ.category.slice(1)} Impact
          </span>
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-900 mb-2">Instructions:</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>No:</strong> The statement is not true for you</li>
          <li>• <strong>Sometimes:</strong> The statement is occasionally true</li>
          <li>• <strong>Yes:</strong> The statement is often or always true</li>
        </ul>
      </div>
    </div>
  );
};

export default Questionnaire;
